import { useRead, ReadState, isCollection } from "@/components/LoadingStates";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ConfirmAction, { describeFollowUp } from "@/components/ConfirmAction";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { timeAgo, classNames } from "@/lib/format";
import { MessagesSquare, Trash2, Bookmark, BookmarkX, Lock, Gavel, AtSign, VideoOff, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { focusTemporarily } from "@/lib/focus";
import FollowUpControls from "@/components/FollowUpControls";
import { useInvalidateMyFollowUps } from "@/hooks/useMyFollowUps";

const initials = (name = "") => name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("") || "?";

// OP-037: mention_delivery is recorded after the reply, per recipient. "sent" only means the provider
// accepted the request; receipt is never known. Recipient addresses are not displayed.
const DELIVERY = { sent: "accepté par le fournisseur (réception non confirmée)", failed: "échec d’envoi", pending_provider: "non envoyé (aucun fournisseur configuré)" };
function mentionDelivery(entries) {
  if (!Array.isArray(entries) || !entries.length) return "Mention enregistrée. Aucun état d’e-mail enregistré : aucun envoi n’est confirmé.";
  const counts = {};
  for (const e of entries) { const label = DELIVERY[e?.status] || "état inconnu"; counts[label] = (counts[label] || 0) + 1; }
  return `Mention enregistrée. E-mail : ${Object.entries(counts).map(([label, n]) => `${n} ${label}`).join(", ")}.`;
}

function Body({ text, mentions = [] }) {
  if (!mentions.length) return <>{text}</>;
  const names = mentions.map((m) => m.name).filter(Boolean).sort((a, b) => b.length - a.length);
  const re = new RegExp(`(@(?:${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}))`, "g");
  return <>{text.split(re).map((part, i) => part.startsWith("@") && names.includes(part.slice(1))
    ? <span key={i} className="text-primary font-semibold bg-primary/10 rounded px-0.5" data-testid="mention-chip">{part}</span>
    : part)}</>;
}

function MentionPicker({ members, query, onPick }) {
  const list = members.filter((m) => m.name.toLowerCase().includes(query.toLowerCase())).slice(0, 6);
  if (!list.length) return null;
  return (
    <div className="absolute left-0 bottom-full mb-1 z-20 w-64 rounded-lg border border-border bg-popover shadow-lg p-1" data-testid="mention-picker">
      {list.map((m) => (
        <button key={m.id} type="button" onMouseDown={(e) => { e.preventDefault(); onPick(m); }} className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent" data-testid={`mention-option-${m.id}`}>
          <span className="h-6 w-6 rounded-full bg-muted text-[10px] font-bold flex items-center justify-center">{initials(m.name)}</span>
          <span className="flex-1 truncate">{m.name}</span>
          <span className="text-[10px] text-muted-foreground uppercase">{m.role}</span>
        </button>
      ))}
    </div>
  );
}

// OP-036: `standalone` is the /threads/{video_id} page. It needs neither the catalogue video nor Saved, works in
// personal and workspace scope, and says why the video page may be unreachable. Decisions and follow-ups stay
// writable by editors and owners outside Saved (as the API allows); posting a new note still needs Saved.
export default function CommentThread({ videoId, saved, onCountChange, standalone = false }) {
  const { user, role, canEdit } = useAuth();
  const threadRead = useRead(`/videos/${videoId}/comments?${new URLSearchParams({ tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC" })}`, (d) => d && isCollection(d.items) && d.items.every((c) => typeof c.body === "string") && isCollection(d.decisions) && isCollection(d.members) && isCollection(d.assignees) && typeof d.total === "number", String(saved));
  const thread = threadRead.data;
  const setThread = threadRead.setData;
  const [draft, setDraft] = useState("");
  const [mentions, setMentions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [posting, setPosting] = useState(false);
  // The exact comment a confirmation describes; cleared on any context change (the read key).
  const [pending, setPending] = useState(null);
  const inputRef = useRef(null);
  // F-NAV-1: surviving destination when a confirmed deletion removes the control that opened its dialog.
  const threadCard = useRef(null);
  const threadFocusFallback = () => { if (threadCard.current?.isConnected) focusTemporarily(threadCard.current); };
  const invalidateFollowUps = useInvalidateMyFollowUps();

  const apply = (data) => { setThread(data); onCountChange?.(data.total); };
  const load = () => threadRead.reload().catch(() => {});
  const applyFollowUp = () => load();

  useEffect(() => {
    if (threadRead.status === "success") onCountChange?.(thread.total);
  }, [threadRead.status, thread?.total, onCountChange]);

  useEffect(() => {
    api.post(`/videos/${videoId}/comments/read`).catch(() => {});
  }, [videoId, saved, user?.workspace_id]);

  const onDraftChange = (e) => {
    const value = e.target.value;
    setDraft(value);
    const before = value.slice(0, e.target.selectionStart);
    const m = before.match(/(?:^|\s)@([\w.-]*)$/);
    setMentionQuery(m ? m[1] : null);
  };

  const pickMention = (m) => {
    const el = inputRef.current;
    const caret = el ? el.selectionStart : draft.length;
    const before = draft.slice(0, caret).replace(/@[\w.-]*$/, `@${m.name} `);
    setDraft(before + draft.slice(caret));
    setMentions((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
    setMentionQuery(null);
    setTimeout(() => { el?.focus(); el?.setSelectionRange(before.length, before.length); }, 0);
  };

  const post = async () => {
    // Button and Ctrl/⌘+Enter both land here: never send when the server or the catalogue rules posting out.
    if (!canEdit || !thread?.saved || !thread?.can_post || thread.video_available === false || posting || !draft.trim()) return;
    setPosting(true);
    try {
      const ids = mentions.filter((m) => draft.includes(`@${m.name}`)).map((m) => m.id);
      const { data } = await api.post(`/videos/${videoId}/comments`, { body: draft.trim(), mentions: ids });
      const items = [...thread.items, data];
      apply({ ...thread, items, total: items.length });
      setDraft(""); setMentions([]);
      // OP-037: the reply only proves the note and its mentions were stored; e-mail is attempted later, if at all.
      if (ids.length) toast.success(`Note enregistrée avec ${ids.length === 1 ? "1 mention" : `${ids.length} mentions`}. Aucun e-mail n’est confirmé : la mention est visible dans le fil et l’activité.`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setPosting(false);
    }
  };

  useEffect(() => { setPending(null); }, [threadRead.key]);
  const ask = (kind, c) => setPending({ kind, comment: c, key: threadRead.key });
  const confirmPending = () => {
    const p = pending;
    setPending(null);
    if (!p || p.key !== threadRead.key) return;
    return p.kind === "delete" ? remove(p.comment.id) : toggleDecision(p.comment);
  };

  const remove = async (cid) => {
    const comment = thread?.items.find((c) => c.id === cid);
    if (!canEdit || !comment || (comment.author_id !== user?.id && role !== "owner")) return;
    try {
      await api.delete(`/videos/${videoId}/comments/${cid}`);
      const items = thread.items.filter((c) => c.id !== cid);
      if (thread.items.find((c) => c.id === cid)?.follow_up) invalidateFollowUps();
      apply({ ...thread, items, total: items.length, decisions: thread.decisions.filter((c) => c.id !== cid) });
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const toggleDecision = async (c) => {
    if (!canEdit || !thread?.can_decide) return;
    try {
      const { data } = await api.patch(`/videos/${videoId}/comments/${c.id}/decision`, { is_decision: !c.is_decision });
      const items = thread.items.map((x) => (x.id === c.id ? data : x));
      const decisions = items.filter((x) => x.is_decision).sort((a, b) => (b.decision_at || b.created_at).localeCompare(a.decision_at || a.created_at));
      apply({ ...thread, items, decisions });
      if (c.follow_up || data.follow_up) invalidateFollowUps();
      toast.success(data.is_decision ? "Pinned as the team decision" : "Decision unpinned");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  if (threadRead.status !== "success") return <ReadState read={threadRead} scope="comment-thread" />;
  const canDecide = canEdit && thread.can_decide;
  const shared = !!user?.workspace_id;
  const target = pending && pending.key === threadRead.key ? pending : null;
  const unavailable = thread.video_available === false;

  return (
    <ReadState read={threadRead} scope="comment-thread">
    <Card className="p-5 space-y-4" data-testid="comment-thread" ref={threadCard}>
      {standalone && (
        <div className="space-y-2" data-testid="thread-context">
          <h1 className="font-display text-2xl font-bold leading-tight" data-testid="thread-title">{thread.video_title || "Vidéo sans titre enregistré"}</h1>
          {unavailable ? (
            <p className="flex items-start gap-2 rounded-lg border border-border p-3 text-sm" role="note" data-testid="thread-video-unavailable">
              <VideoOff className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span><b>Video unavailable</b> — cette vidéo n’est plus dans le catalogue. Le titre affiché est celui enregistré sur les notes ; aucune métrique n’est disponible.</span>
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {!thread.saved && <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold" data-testid="thread-removed-from-saved"><BookmarkX className="h-3 w-3" aria-hidden="true" />Removed from Saved</span>}
              <Link to={`/videos/${encodeURIComponent(videoId)}`} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="thread-video-link"><ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />Ouvrir la fiche vidéo</Link>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-display font-semibold flex items-center gap-2"><MessagesSquare className="h-4 w-4 text-primary" /> Team thread</h4>
        <span className="text-xs font-mono text-muted-foreground" data-testid="comment-thread-count">{thread.total} {thread.total === 1 ? "note" : "notes"}</span>
      </div>

      {thread.decisions?.length > 0 && (
        <div className="space-y-2" data-testid="decision-block">
          {thread.decisions.map((d, i) => (
            <div key={d.id} className={classNames("rounded-lg border-l-4 border-[hsl(var(--amber))] bg-[hsl(var(--amber))]/10 px-3 py-2", i > 0 && "opacity-80")} data-testid={`decision-item-${d.id}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--amber))]">
                <Gavel className="h-3 w-3" /> {i === 0 ? "Latest decision" : "Decision"}
                <span className="text-muted-foreground font-medium normal-case tracking-normal">· {d.decision_by_name || d.author_name} · {timeAgo(d.decision_at || d.created_at)}</span>
              </div>
              <p className="text-sm mt-1 whitespace-pre-wrap break-words"><Body text={d.body} mentions={d.mentions} /></p>
              <div className="mt-1.5">
                <FollowUpControls videoId={videoId} comment={d} members={thread.assignees || []} canEdit={canDecide} onChange={applyFollowUp} currentUserId={user?.id} />
              </div>
            </div>
          ))}
        </div>
      )}

      {thread.items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4 text-center" data-testid="comment-thread-empty">
          {thread.saved ? (canEdit && !unavailable ? "Aucune note. Vous pouvez commencer la discussion ci-dessous." : "Aucune note dans ce fil d’équipe.") : "Les fils d’équipe concernent les vidéos sauvegardées."}
        </div>
      ) : (
        <ol className="space-y-3" data-testid="comment-list">
          {thread.items.map((c) => {
            const mine = c.author_id === user?.id;
            const canDelete = canEdit && (mine || role === "owner");
            return (
              <li key={c.id} className="flex gap-3 group" data-testid={`comment-item-${c.id}`}>
                <div className={classNames("h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold", mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground")}>{initials(c.author_name)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold text-foreground">{c.author_name || "Teammate"}</span>
                    <span className="text-muted-foreground">{timeAgo(c.created_at)}</span>
                    {c.is_decision && <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--amber))]/15 text-[hsl(var(--amber))] px-1.5 py-0.5 text-[10px] font-bold uppercase" data-testid={`comment-decision-badge-${c.id}`}><Gavel className="h-2.5 w-2.5" /> Decision</span>}
                    <span className="ml-auto flex items-center gap-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      {canDecide && (
                        <button onClick={() => (c.is_decision ? ask("unmark", c) : toggleDecision(c))} className={classNames("inline-flex items-center gap-1 hover:text-[hsl(var(--amber))]", c.is_decision ? "text-[hsl(var(--amber))]" : "text-muted-foreground")} title={c.is_decision ? "Unmark decision" : "Mark as decision"} data-testid={`comment-decision-toggle-${c.id}`}>
                          <Gavel className="h-3.5 w-3.5" /><span className="hidden sm:inline">{c.is_decision ? "Unmark" : "Mark decision"}</span>
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => ask("delete", c)} className="text-muted-foreground hover:text-[hsl(var(--rose))]" title="Delete note" data-testid={`comment-delete-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap break-words mt-0.5"><Body text={c.body} mentions={c.mentions} /></p>
                  {c.mentions?.length > 0 && <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`comment-mention-delivery-${c.id}`}>{mentionDelivery(c.mention_delivery)}</p>}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {thread.truncated && <p className="text-xs text-muted-foreground" role="note" data-testid="comment-thread-truncated">Fil limité aux {thread.limit} notes les plus anciennes : les plus récentes ne sont pas affichées.</p>}
      {unavailable ? (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground" data-testid="comment-unavailable-hint">
          <VideoOff className="h-4 w-4 shrink-0" /> {thread.saved ? "Vidéo indisponible dans le catalogue : aucune nouvelle note ne peut être publiée. Les décisions et suivis restent modifiables selon vos droits." : "Vidéo indisponible dans le catalogue : elle ne peut pas être sauvegardée, et aucune nouvelle note ne peut être publiée."}
        </div>
      ) : !thread.saved ? (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground" data-testid="comment-save-hint">
          <Bookmark className="h-4 w-4 shrink-0" /> Sauvegardez manuellement cette vidéo dans la bibliothèque avant de publier une note. Aucune sauvegarde automatique n’est effectuée.
        </div>
      ) : !canEdit || !thread.can_post ? (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground" data-testid="comment-readonly-hint">
          <Lock className="h-4 w-4 shrink-0" /> Vous pouvez consulter ce fil, mais vos accès ne permettent pas de publier.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="relative">
            {mentionQuery !== null && <MentionPicker members={thread.members || []} query={mentionQuery} onPick={pickMention} />}
            <Textarea
              ref={inputRef}
              value={draft}
              onChange={onDraftChange}
              onKeyDown={(e) => { if (e.key === "Escape") setMentionQuery(null); if ((e.metaKey || e.ctrlKey) && e.key === "Enter") post(); }}
              onBlur={() => setTimeout(() => setMentionQuery(null), 150)}
              placeholder="Leave a note for the team — type @ to mention a teammate…"
              className="min-h-20"
              maxLength={2000}
              data-testid="comment-input"
            />
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><AtSign className="h-3 w-3" /> @ to mention · ⌘/Ctrl + Enter to post</span>
            <Button size="sm" onClick={post} disabled={posting || !draft.trim()} data-testid="comment-post-button">{posting ? "Posting…" : "Post note"}</Button>
          </div>
        </div>
      )}
      <ConfirmAction open={target?.kind === "delete"} onClose={() => setPending(null)} onConfirm={confirmPending} testid="comment-delete-dialog"
        contextKey={threadRead.key} restoreFocusFallback={threadFocusFallback}
        title="Supprimer définitivement cette note ?" confirmLabel="Supprimer la note">
        <p>{target?.comment.author_id === user?.id ? "Votre note" : `La note de ${target?.comment.author_name || "ce membre"}`} sera supprimée{shared ? " pour tous les membres de l’espace" : ""}, sans restauration possible.</p>
        {target?.comment.is_decision && <p data-testid="comment-delete-dialog-decision">Elle est marquée comme décision : la décision sera supprimée aussi{target.comment.follow_up ? `, ainsi que son suivi (${describeFollowUp(target.comment.follow_up)})` : ""}.</p>}
        {shared && <p>Seul un extrait de la note restera dans l’activité de l’espace.</p>}
      </ConfirmAction>
      <ConfirmAction open={target?.kind === "unmark"} onClose={() => setPending(null)} onConfirm={confirmPending} testid="comment-unmark-dialog"
        contextKey={threadRead.key} restoreFocusFallback={threadFocusFallback}
        title="Retirer la décision ?" confirmLabel="Retirer la décision">
        <p>La note reste dans le fil ; la mention « décision marquée par {target?.comment.decision_by_name || target?.comment.author_name || "un membre"} » sera effacée.</p>
        {target?.comment.follow_up && <p data-testid="comment-unmark-dialog-follow-up">Son suivi sera effacé aussi ({describeFollowUp(target.comment.follow_up)}), sans restauration possible ; seul un événement d’activité restera.</p>}
      </ConfirmAction>
    </Card>
    </ReadState>
  );
}
